<template>
  <div>
    <div class="form-row">
      <div class="form-group col-md">
        <treeselect
          ref="structureTreeselect"
          instance-id="structure"
          v-model="structureValue"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.StructureFilter.Placeholder')"
          :options="structureItems"
          :disabled="disabledInternal"
          :disableFuzzyMatching="true"
          :multiple="multipleSurveySelectionInternal"
          :disable-branch-nodes="!multipleSurveySelectionInternal"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          valueFormat="object"
          @close="loadData"
          @input="filterInput"
        >
        </treeselect>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group col-md">
        <treeselect
          ref="waveTreeselect"
          instance-id="wave"
          v-model="waveValue"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.WaveFilter.Placeholder')"
          :options="waveItems"
          :disabled="disabledInternal || waveSelectDisabled"
          :disableFuzzyMatching="true"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :flat="true"
          :defaultExpandLevel="1"
          :disable-branch-nodes="true"
          :limit="4"
          :limit-text="limitText"
          @close="loadData"
          @input="filterInput"
        >
        </treeselect>
      </div>
      <div class="form-group col-md">
        <treeselect
          ref="scenarioTreeselect"
          instance-id="scenario"
          v-model="scenarioValue"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
          :options="scenarioItems"
          :disabled="disabledInternal || scenarioSelectDisabled"
          :disableFuzzyMatching="true"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :flat="true"
          :defaultExpandLevel="1"
          :disable-branch-nodes="true"
          :limit="4"
          :limit-text="limitText"
          @close="loadData"
          @input="filterInput"
        >
        </treeselect>
      </div>
      <div class="form-group col-md">
        <treeselect
          ref="propertyTreeselect"
          instance-id="property"
          v-model="propertyValue"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
          :options="propertyItems"
          :disableFuzzyMatching="true"
          :disabled="disabledInternal || propertySelectDisabled"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :defaultExpandLevel="1"
          :disable-branch-nodes="false"
          value-consists-of="LEAF_PRIORITY"
          :limit="4"
          :limit-text="limitText"
          @close="loadData"
          @input="filterInput"
        >
        </treeselect>
      </div>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService';

export default {
  name: "SurveyFilter",

  mixins: [ AppConfig ],

  props: {
    value: null,
    disabled: { default: false },
    multipleSurveySelection: { default: true }
  },

  data() {
    return {
      disabledInternal: this.disabled,
      multipleSurveySelectionInternal: this.multipleSurveySelection,

      structureItems: [],                   // Data items for structure treeselect
      structureValue: this.multipleSurveySelectionInternal ? [] : null, // Selected structure items
      waveItems: [],                        // Data items for wave treeselect
      waveValue: [],                        // Selected wave items
      scenarioItems: [],                    // Data items for scenarios
      scenarioValue: [],                    // Selected scenarios
      propertyItems: [],                    // Data items for property treeselect
      propertyValue: [],                    // Selected property items

      currentStructureValue: null,          // Current selected structure items for change detection
      currentFilter: null,                  // Current filter object for change detection
    }
  },

  computed: {
    waveSelectDisabled() {
      return !this.multipleSurveySelectionInternal && _.isEmpty(this.structureValue);
    },

    scenarioSelectDisabled() {
      return !this.multipleSurveySelectionInternal && _.isEmpty(this.structureValue);
    },

    propertySelectDisabled() {
      return _.isEmpty(this.structureValue);
    },

    filter() {
      return {
        clientIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'C').map(item => item.id.substring(1)) : [],
        projectIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'P').map(item => item.id.substring(1)) : [],
        surveyIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'S').map(item => item.id.substring(1)) : 
          this.structureValue ? [ this.structureValue.id.substring(1) ] : [],
        // Includes survey ids from selected clients and projects
        computedSurveyIds: Array.isArray(this.structureValue) ?
          _.flatMapDeep(
            _.flatMapDeep(
              this.structureValue,
              item => item.id[0] !== 'S' ? item.children : item),
            item => item.id[0] !== 'S' ? item.children : item)
          .filter(item => item && item.id[0] === 'S')
          .map(item => item.id.substring(1)) :
          this.structureValue ? [ this.structureValue.id.substring(1) ] : [],
        waveIds: this.waveValue,
        scenarioIds: this.scenarioValue,
        propertyIds: this.propertyValue,
        propertyUseLogicalAnd: false
      };
    },
  },

  watch: {
    currentFilter(newVal, oldVal) {
      this.$emit("input", newVal);
    },

    disabled(newVal, oldVal) {
      this.disabledInternal = newVal;
    },

    disabledInternal(newVal, oldVal) {
      this.$emit("disabledChanged", newVal);
    },
  },

  mounted() {
    this.currentFilter = this.filter;

    this.getSurveyStructure();
    this.getSurveyWaves();
    this.getSurveyScenarios();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyStructure();
      this.getSurveyWaves();
      this.getSurveyScenarios();
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyWaves() {
      if (!this.waveSelectDisabled)
      {
        this.waveItems = [];
        SurveysService.getSurveyWaves(this.filter)
          .then(response => {
            this.waveItems = response.data;
          })
      }
    },

    getSurveyScenarios() {
      if (!this.scenarioSelectDisabled)
      {
        this.scenarioItems = [];
        SurveysService.getSurveyScenarios(this.filter)
          .then(response => {
            this.scenarioItems = response.data;
          })
      }
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.propertySelectDisabled)
      {
        this.propertyItems = [];
        SurveysService.getSurveyCustomProperties(this.filter, true)
          .then(response => {
            this.propertyItems = response.data;
          })
      }
    },

    // Called whenever the input of a treeselect control changes
    filterInput(value, instanceId) {
      // Workaround: Only call data loading when clear all is pressed
      if (_.size(value) === 0 || (!this.multipleSurveySelection && instanceId == "structure"))
        this.loadData();
    },

    // Applies changed filter and triggeres data loading if necessary
    loadData() {
      // Only load data when effective filter has changed
      if (!_.isEqual(this.filter, this.currentFilter)) {
        let structureValueChanged = !_.isEqual(this.currentStructureValue, this.structureValue);
        this.currentStructureValue = this.structureValue;
        if (structureValueChanged)
        {
          this.waveValue = [];
          this.propertyValue = [];
          this.scenarioValue = [];
        }

        this.currentFilter = this.filter;

        if (structureValueChanged)
        {
          this.getSurveyWaves();
          this.getSurveyProperties();
          this.getSurveyScenarios();
        }
      }
    },
  }
}
</script>
